// Debounced render pipeline: turns "something changed" into "eventually one
// GPU frame gets drawn." Configured once from app.mjs with the DOM refs and
// device it needs; every pan/zoom/control listener elsewhere just calls
// queueRender() with no arguments.

import { renderFractal } from "../fractals/render.mjs";
import { blitToCanvas } from "../util/pixels.mjs";
import { state, activeShaderFormula } from "./viewState.mjs";

let canvas, canvasCtx, statusEl, renderTimeEl, device;
let renderQueued = false;

/** Wires up the DOM elements this module renders into. Call once at startup. */
export function configureRenderLoop(refs) {
  ({ canvas, canvasCtx, statusEl, renderTimeEl } = refs);
}

/** Sets the GPU device to render with. Call once after init() resolves. */
export function setDevice(d) {
  device = d;
}

/**
 * Schedules a render on the next animation frame. Safe to call many times
 * per frame (drag, wheel) — extra calls before the frame fires are no-ops.
 */
export function queueRender() {
  if (renderQueued) return;
  renderQueued = true;
  requestAnimationFrame(async () => {
    renderQueued = false;
    await renderNow();
  });
}

/** Renders one frame with the current `state` and blits it to the canvas. */
export async function renderNow() {
  // init() failed — its error is already shown in #status. Pan/zoom/slider
  // listeners are wired up before init() resolves, so without this guard a
  // stray interaction after a failed init would reach getPipeline(undefined,
  // ...) and crash on the WeakMap key instead of just leaving the real error visible.
  if (!device) return;

  statusEl.textContent = "";
  const t0 = performance.now();
  try {
    const pixels = await renderFractal(device, activeShaderFormula(), {
      width: canvas.width,
      height: canvas.height,
      centerRe: state.centerRe,
      centerIm: state.centerIm,
      scale: state.scale,
      maxIter: state.maxIter,
      extra: state.extra,
    });
    blitToCanvas(canvasCtx, pixels, canvas.width, canvas.height);
    renderTimeEl.textContent = `${(performance.now() - t0).toFixed(0)} ms`;
  } catch (err) {
    statusEl.textContent = err.message;
    console.error(err);
  }
}
